import React, { useState, useEffect } from 'react';
import { User, Bell, Shield, Database, Download, Upload, Trash2, Save, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useAuthStore } from '../../store/authStore';
import { db } from '../../lib/db';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import bcrypt from 'bcryptjs';
import { useConfirmDialog } from './ConfirmDialog';

export default function SettingsPage() {
  const { user, updateUser } = useAuthStore();
  const { confirm } = useConfirmDialog();
  const [activeTab, setActiveTab] = useState('profile');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: 'HQ'
  });
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [notifications, setNotifications] = useState({ email: true, appraisalReminders: true, deadlineAlerts: true, teamUpdates: false });

  useEffect(() => {
    if (user) {
      setProfile({
        firstName: user.profile?.firstName || '',
        lastName: user.profile?.lastName || '',
        email: user.email || '',
        phone: user.profile?.phone || '',
        location: user.profile?.location || 'HQ'
      });
    }
  }, [user]);

  const handleSaveProfile = async () => {
    if (!profile.firstName || !profile.lastName) {
      toast.error('First name and last name are required');
      return;
    }
    setLoading(true);
    try {
      const updates = {
        email: profile.email,
        profile: {
          ...user.profile,
          firstName: profile.firstName,
          lastName: profile.lastName,
          phone: profile.phone,
          location: profile.location
        },
        updatedAt: Date.now()
      };
      await db.users.update(user.id, updates);
      updateUser(updates);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      toast.error('Please fill in all password fields');
      return;
    }
    if (passwords.new !== passwords.confirm) {
      toast.error('New passwords do not match');
      return;
    }
    if (passwords.new.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      const currentUser = await db.users.get(user.id);
      const isValid = await bcrypt.compare(passwords.current, currentUser.passwordHash);
      if (!isValid) {
        toast.error('Current password is incorrect');
        setLoading(false);
        return;
      }
      const newHash = await bcrypt.hash(passwords.new, 10);
      await db.users.update(user.id, { passwordHash: newHash, updatedAt: Date.now() });
      toast.success('Password changed successfully');
      setShowPasswordModal(false);
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (error) {
      toast.error('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = async () => {
    try {
      const [users, appraisals, templates, goals, events, invites] = await Promise.all([
        db.users.toArray(),
        db.appraisals.toArray(),
        db.templates.toArray(),
        db.goals.toArray(),
        db.calendarEvents.toArray(),
        db.invites.toArray()
      ]);
      const data = { users, appraisals, templates, goals, events, invites, exportedAt: new Date().toISOString() };
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pms_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleImportData = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      if (data.users) await db.users.bulkPut(data.users);
      if (data.appraisals) await db.appraisals.bulkPut(data.appraisals);
      if (data.templates) await db.templates.bulkPut(data.templates);
      if (data.goals) await db.goals.bulkPut(data.goals);
      if (data.events) await db.calendarEvents.bulkPut(data.events);
      if (data.invites) await db.invites.bulkPut(data.invites);
      toast.success('Data imported successfully. Please refresh the page.');
    } catch (error) {
      toast.error('Failed to import data. Please check the file format.');
    }
    event.target.value = '';
  };

  const handleClearData = async () => {
    const confirmed = await confirm({
      title: 'Clear All Data',
      description: 'Are you sure you want to clear ALL data? This cannot be undone!',
      confirmText: 'Clear All Data',
      variant: 'destructive'
    });
    if (!confirmed) return;
    try {
      await Promise.all([
        db.users.clear(),
        db.appraisals.clear(),
        db.templates.clear(),
        db.goals.clear(),
        db.calendarEvents.clear(),
        db.invites.clear(),
        db.notifications.clear()
      ]);
      toast.success('All data cleared. Please refresh and log in again.');
    } catch (error) {
      toast.error('Failed to clear data');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500 mt-1">Manage your account and preferences</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100 p-1">
          <TabsTrigger value="profile" className="data-[state=active]:bg-white"><User className="w-4 h-4 mr-2" /> Profile</TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-white"><Bell className="w-4 h-4 mr-2" /> Notifications</TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-white"><Shield className="w-4 h-4 mr-2" /> Security</TabsTrigger>
          {user?.role === 'admin' && <TabsTrigger value="data" className="data-[state=active]:bg-white"><Database className="w-4 h-4 mr-2" /> Data</TabsTrigger>}
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Personal Information</CardTitle><CardDescription>Update your personal details</CardDescription></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center gap-6">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                  {profile.firstName?.[0]}{profile.lastName?.[0]}
                </div>
                <div>
                  <p className="text-sm text-gray-500">Profile picture is generated from initials</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input value={profile.firstName} onChange={(e) => setProfile({...profile, firstName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input value={profile.lastName} onChange={(e) => setProfile({...profile, lastName: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={profile.email} onChange={(e) => setProfile({...profile, email: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input value={profile.phone} onChange={(e) => setProfile({...profile, phone: e.target.value})} />
                </div>
                <div className="space-y-2">
                  <Label>Department</Label>
                  <Input value={user?.profile?.department || ''} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Position</Label>
                  <Input value={user?.profile?.position || ''} disabled />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Select value={profile.location} onValueChange={(v) => setProfile({...profile, location: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="HQ">HQ</SelectItem>
                      <SelectItem value="Remote">Remote</SelectItem>
                      <SelectItem value="Branch">Branch Office</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSaveProfile} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">
                  <Save className="w-4 h-4 mr-2" /> {loading ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-6 mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Notification Preferences</CardTitle><CardDescription>Choose what notifications you want to receive</CardDescription></CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-gray-900">Email Notifications</p><p className="text-sm text-gray-500">Receive notifications via email</p></div>
                <Switch checked={notifications.email} onCheckedChange={(v) => setNotifications({...notifications, email: v})} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-gray-900">Appraisal Reminders</p><p className="text-sm text-gray-500">Get reminded about pending appraisals</p></div>
                <Switch checked={notifications.appraisalReminders} onCheckedChange={(v) => setNotifications({...notifications, appraisalReminders: v})} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-gray-900">Deadline Alerts</p><p className="text-sm text-gray-500">Notify before deadlines</p></div>
                <Switch checked={notifications.deadlineAlerts} onCheckedChange={(v) => setNotifications({...notifications, deadlineAlerts: v})} />
              </div>
              <div className="flex items-center justify-between">
                <div><p className="font-medium text-gray-900">Team Updates</p><p className="text-sm text-gray-500">Notifications about team activities</p></div>
                <Switch checked={notifications.teamUpdates} onCheckedChange={(v) => setNotifications({...notifications, teamUpdates: v})} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-6 mt-6">
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Password</CardTitle><CardDescription>Manage your password</CardDescription></CardHeader>
            <CardContent>
              <Button variant="outline" onClick={() => setShowPasswordModal(true)}>Change Password</Button>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardHeader><CardTitle>Sessions</CardTitle><CardDescription>Manage your active sessions</CardDescription></CardHeader>
            <CardContent>
              <div className="p-4 bg-gray-50 rounded-lg flex items-center justify-between">
                <div><p className="font-medium text-gray-900">Current Session</p><p className="text-sm text-gray-500">Started just now • This device</p></div>
                <Badge variant="secondary" className="bg-emerald-100 text-emerald-700">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {user?.role === 'admin' && (
          <TabsContent value="data" className="space-y-6 mt-6">
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle>Export Data</CardTitle><CardDescription>Download all your organization's data</CardDescription></CardHeader>
              <CardContent>
                <Button onClick={handleExportData} variant="outline"><Download className="w-4 h-4 mr-2" /> Export to JSON</Button>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm">
              <CardHeader><CardTitle>Import Data</CardTitle><CardDescription>Restore data from a backup</CardDescription></CardHeader>
              <CardContent>
                <label>
                  <input type="file" accept=".json" onChange={handleImportData} className="hidden" />
                  <Button variant="outline" asChild><span><Upload className="w-4 h-4 mr-2" /> Import from JSON</span></Button>
                </label>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-sm border-rose-200">
              <CardHeader><CardTitle className="text-rose-600">Danger Zone</CardTitle><CardDescription>Irreversible actions</CardDescription></CardHeader>
              <CardContent>
                <Button onClick={handleClearData} variant="outline" className="border-rose-300 text-rose-600 hover:bg-rose-50"><Trash2 className="w-4 h-4 mr-2" /> Clear All Data</Button>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      <Dialog open={showPasswordModal} onOpenChange={setShowPasswordModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Change Password</DialogTitle><DialogDescription>Enter your current and new password</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current Password</Label>
              <div className="relative">
                <Input type={showPassword ? 'text' : 'password'} value={passwords.current} onChange={(e) => setPasswords({...passwords, current: e.target.value})} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={passwords.new} onChange={(e) => setPasswords({...passwords, new: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input type="password" value={passwords.confirm} onChange={(e) => setPasswords({...passwords, confirm: e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPasswordModal(false)}>Cancel</Button>
            <Button onClick={handleChangePassword} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">{loading ? 'Updating...' : 'Update Password'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
